import { useState } from 'react'
import ProgressBar from './ProgressBar.jsx'
import HsBadge from './HsBadge.jsx'
import EditableValue from './EditableValue.jsx'
import SubgoalOwnersPicker from './SubgoalOwnersPicker.jsx'
import CommentBubble from './CommentBubble.jsx'
import ReactionBubble from './ReactionBubble.jsx'
import CarryForwardButton from './CarryForwardButton.jsx'
import SubgoalContributorsDrawer from './SubgoalContributorsDrawer.jsx'
import { SUBGOAL_GRID } from './subgoalGrid.js'
import { calcPct, fmt, statusColor } from '../data/format.js'
import { useGoalsCrud } from '../mutations/useGoalsCrud.js'
import { useMetaData } from '../queries/useMetaData.js'
import { useGoalsData } from '../queries/useGoalsData.js'
import { useUpdateGoal } from '../mutations/useUpdateGoal.js'
import { useIsReadOnly } from '../store/archiveViewStore.js'
import { pillarIndexByName } from '../data/metaKeys.js'

/**
 * SubGoalRow — row inside a goal's sub-goal table.
 *
 * Layout matches the live q2-goals.html dashboard:
 *   | Sub-Goal · HS badge | Owner | Current | Target | Progress | % | Update (yellow) · 💬 · 🙂 |
 *
 * The yellow-tinted Update textarea on the right is the visual cue that
 * "this is where you write your update." Color comes from the old dashboard.
 */
export default function SubGoalRow({ sub, pillarName, goalIndex, subgoalIndex }) {
  const [hovered, setHovered] = useState(false)
  const contributors = Array.isArray(sub.contributors) ? sub.contributors : []
  const manualContributors = Array.isArray(sub.manualContributors) ? sub.manualContributors : []
  const hasContributors = contributors.length > 0 || manualContributors.length > 0 || !!sub.hsSync
  const { removeSubgoal } = useGoalsCrud()
  const { data: meta } = useMetaData()
  const { data: goalsData } = useGoalsData()
  const updateGoal = useUpdateGoal()
  const readOnly = useIsReadOnly()
  const pct = calcPct(sub.current, sub.target, sub.lowerIsBetter)
  const color = statusColor(pct)

  // Carry-forward: look up last snapshot's update text for this
  // specific sub-goal. Shown only when the current update is empty
  // AND last week's snapshot had content. We look at the GOALS array
  // frozen in the snapshot (not the fieldEdits log) so the preview
  // shows the user's actual saved text. Derive pillarIndex from the
  // live goals data so this works regardless of pillar order.
  const pillarIndex = pillarIndexByName(goalsData, pillarName)
  const lastSnapshotWeek = meta?.lastSnapshotWeek
  const lastGoals = lastSnapshotWeek ? meta?.weeklyHistory?.[lastSnapshotWeek]?.goals : null
  const priorUpdate =
    pillarIndex >= 0 && Array.isArray(lastGoals)
      ? (lastGoals[pillarIndex]?.goals?.[goalIndex]?.subgoals?.[subgoalIndex]?.update
        || lastGoals[pillarIndex]?.goals?.[goalIndex]?.subGoals?.[subgoalIndex]?.update
        || '')
      : ''
  const showCarryForward = !readOnly && !sub.update && priorUpdate

  const applyCarryForward = () => {
    if (!priorUpdate) return
    updateGoal.mutate({
      pillarName,
      goalIndex,
      subgoalIndex,
      field: 'update',
      value: priorUpdate,
    })
  }

  const handleDelete = () => {
    if (confirm(`Delete sub-goal "${sub.subgoal}"?`)) {
      removeSubgoal({ pillarName, goalIndex, subgoalIndex })
    }
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ borderBottom: '1px solid var(--border)' }}
    >
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: SUBGOAL_GRID,
        gap: 10,
        alignItems: 'center',
        padding: '8px 12px',
        fontSize: 12,
      }}
    >
      {/* 1. Sub-goal label — two stacked lines:
             [label                                X]
             [HubSpot badge]
          The delete X is right-justified inside the cell so it can't
          be confused with the label itself. The badge moves below the
          label so it doesn't crowd the click target. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, minWidth: 0 }}>
          <span style={{ fontWeight: 600, color: 'var(--text-secondary)', lineHeight: 1.4, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <EditableValue
              pillarName={pillarName}
              goalIndex={goalIndex}
              subgoalIndex={subgoalIndex}
              field="subgoal"
              value={sub.subgoal}
              inputType="text"
            />
          </span>
          <button
            type="button"
            onClick={handleDelete}
            title={`Delete sub-goal "${sub.subgoal}"`}
            aria-label="Delete sub-goal"
            style={{
              width: 18,
              height: 18,
              border: 'none',
              background: 'transparent',
              color: 'var(--muted-light)',
              fontSize: 11,
              cursor: 'pointer',
              opacity: hovered ? 0.7 : 0,
              transition: 'opacity 0.15s, color 0.15s, background 0.15s',
              borderRadius: 4,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '1'
              e.currentTarget.style.color = '#dc2626'
              e.currentTarget.style.background = '#fee2e2'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = hovered ? '0.7' : '0'
              e.currentTarget.style.color = 'var(--muted-light)'
              e.currentTarget.style.background = 'transparent'
            }}
          >
            ✕
          </button>
        </div>
        {sub.hsSync && <div><HsBadge /></div>}
      </div>

      {/* 2. Owner — multi-select picker. Single owner shows a colored
          pill (same look as before); multiple owners show "First +N"
          in neutral gray with full list in the tooltip + dropdown. */}
      <SubgoalOwnersPicker
        pillarName={pillarName}
        goalIndex={goalIndex}
        subgoalIndex={subgoalIndex}
        value={sub.owner}
      />

      {/* 3. Current */}
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: 'var(--text-secondary)',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        <EditableValue
          pillarName={pillarName}
          goalIndex={goalIndex}
          subgoalIndex={subgoalIndex}
          field="current"
          value={sub.current}
          unit={sub.unit}
          formatter={(v) => fmt(v, sub.unit)}
          readonly={Boolean(sub.hsSync)}
        />
      </span>

      {/* 4. Target */}
      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--muted)',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {/* Target always editable — HubSpot pulls current, not target */}
        <EditableValue
          pillarName={pillarName}
          goalIndex={goalIndex}
          subgoalIndex={subgoalIndex}
          field="target"
          value={sub.target}
          unit={sub.unit}
          formatter={(v) => fmt(v, sub.unit)}
        />
      </span>

      {/* 5. Progress bar */}
      <div style={{ minWidth: 0, paddingTop: 4 }}>
        <ProgressBar pct={pct} color={color} />
      </div>

      {/* 6. Percentage */}
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {pct}%
      </span>

      {/* 7. Yellow-tinted Update field — the visual cue for "write here".
           Carry-forward button appears on the right when the field is
           empty and last week had an update. */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <EditableValue
            pillarName={pillarName}
            goalIndex={goalIndex}
            subgoalIndex={subgoalIndex}
            field="update"
            value={sub.update || ''}
            inputType="text"
            multiline
            placeholder="Add an update…"
            style={{
              background: '#fef9c3',
              border: '1px solid #fde68a',
              borderRadius: 6,
              padding: '6px 8px',
              fontSize: 11,
              color: '#713f12',
              lineHeight: 1.5,
              fontStyle: 'normal',
              fontWeight: 500,
              width: '100%',
              minWidth: 0,
              display: 'block',
              margin: 0,
              outline: 'none',
            }}
          />
        </div>
        {showCarryForward && (
          <CarryForwardButton
            compact
            preview={priorUpdate}
            onApply={applyCarryForward}
          />
        )}
      </div>

      {/* 8. Action column — comment + emoji bubbles laid out horizontally */}
      <div
        style={{
          display: 'flex',
          gap: 3,
          alignItems: 'center',
        }}
      >
        <CommentBubble label={sub.subgoal} type="Sub-Goal" />
        <ReactionBubble label={sub.subgoal} />
      </div>
    </div>
    {hasContributors && (
      <SubgoalContributorsDrawer
        contributors={contributors}
        manualContributors={manualContributors}
        pillarName={pillarName}
        goalIndex={goalIndex}
        subgoalIndex={subgoalIndex}
      />
    )}
    </div>
  )
}
